import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_URL from '../api/baseURL';
import VehicleDocumentsCard from '../components/VehicleDocumentsCard';

const WorkshopVehicleHistory = () => {
  const { vehicleId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [history, setHistory] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vehicleId || !token) return;
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: null })),
      axios.get(`${API_URL}/maintenance/history/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => ({ data: [] }))
    ]).then(([vRes, hRes]) => {
      setVehicle(vRes.data);
      setHistory(Array.isArray(hRes.data) ? hRes.data : []);
    }).finally(() => setLoading(false));
  }, [vehicleId, token]);

  // The server allows the owner or a workshop on serviceWorkshops/workshopId.
  // Mirrored here so the Modifier button is absent rather than offering an
  // edit that comes back 403.
  const uid = String(user?._id || '');
  const idOf = (v) => String(v?._id ?? v ?? '');
  const canEditDocuments = !!vehicle && !!uid && (
    idOf(vehicle.owner) === uid
    || idOf(vehicle.workshopId) === uid
    || (vehicle.serviceWorkshops || []).some((w) => idOf(w) === uid)
  );

  const toggleExpand = (id) => {
    setExpandedLogs(prev => ({ ...prev, [id]: !prev[id] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 hover:text-white mb-4 transition-colors text-sm"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Retour
      </button>

      {/* Vehicle header card */}
      {vehicle && (
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold text-white">
                {vehicle.brand} {vehicle.model}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg text-sm font-bold border border-blue-500/30">
                  {vehicle.plateNumber}
                </span>
                {vehicle.year && (
                  <span className="text-slate-400 text-sm">{vehicle.year}</span>
                )}
                {vehicle.vin && (
                  <span className="text-slate-500 text-xs font-mono">{vehicle.vin}</span>
                )}
              </div>
            </div>
            <div className="text-right">
              {vehicle.owner?.name && (
                <div className="text-slate-300 text-sm">
                  <span className="text-slate-500">Client:</span> {vehicle.owner.name}
                </div>
              )}
              {vehicle.owner?.phone && (
                <div className="text-slate-400 text-xs mt-0.5">{vehicle.owner.phone}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assurance + visite technique. The garage is holding the papers, so
          it is the party best placed to keep these two dates current. */}
      {vehicle && (
        <VehicleDocumentsCard
          vehicle={vehicle}
          token={token}
          canEdit={canEditDocuments}
          onSaved={(v) => setVehicle(v)}
        />
      )}

      {/* Maintenance history */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Historique Maintenance
        </h2>
        <span className="text-slate-500 text-sm">{history.length} intervention{history.length !== 1 ? 's' : ''}</span>
      </div>

      {history.length === 0 ? (
        <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-10 text-center">
          <svg className="w-12 h-12 text-slate-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p className="text-slate-500">Aucune intervention enregistrée pour ce véhicule</p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map(entry => {
            const isExpanded = expandedLogs[entry._id];
            const statusColors = {
              confirmed: 'bg-green-500/20 text-green-300 border-green-500/30',
              completed: 'bg-green-500/20 text-green-300 border-green-500/30',
              pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
              cancelled: 'bg-red-500/20 text-red-300 border-red-500/30',
            };
            const statusClass = statusColors[entry.status] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';

            return (
              <div key={entry._id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden transition-all">
                {/* Header row */}
                <div
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-800/80 transition-colors"
                  onClick={() => toggleExpand(entry._id)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-lg shrink-0">
                      {entry.serviceReason === 'Repair' ? '🔧' :
                       entry.serviceReason === 'Oil Change' ? '🛢️' :
                       entry.serviceReason === 'Inspection' ? '🔍' : '🛠️'}
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">
                        {entry.serviceReason || 'Maintenance'}
                      </div>
                      <div className="text-slate-500 text-xs">
                        {entry.date ? new Date(entry.date).toLocaleDateString('fr-FR', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        }) : 'Date inconnue'}
                        {entry.technician && <span> — {entry.technician}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-0.5 rounded text-xs border ${statusClass}`}>
                      {entry.status || 'N/A'}
                    </span>
                    <span className="text-white font-semibold text-sm">
                      {(entry.totalCost || 0).toLocaleString('fr-FR')} FCFA
                    </span>
                    <svg className={`w-4 h-4 text-slate-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t border-slate-700/50 p-4 bg-slate-900/30">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      <div>
                        <span className="text-slate-500 text-xs block">Technicien</span>
                        <span className="text-white text-sm">{entry.technician || '—'}</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs block">Kilométrage</span>
                        <span className="text-white text-sm">
                          {entry.odometerReading != null ? `${entry.odometerReading.toLocaleString('fr-FR')} km` : '—'}
                        </span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs block">Main d'oeuvre</span>
                        <span className="text-white text-sm">{(entry.laborCost || 0).toLocaleString('fr-FR')} FCFA</span>
                      </div>
                      <div>
                        <span className="text-slate-500 text-xs block">Statut</span>
                        <span className="text-white text-sm">{entry.status || '—'}</span>
                      </div>
                    </div>

                    {/* Parts table */}
                    {entry.partsUsed && entry.partsUsed.length > 0 && (
                      <div className="mb-3">
                        <h4 className="text-xs uppercase text-slate-500 font-bold mb-2">Pièces utilisées</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-left text-xs">
                            <thead>
                              <tr className="border-b border-slate-700">
                                <th className="py-2 text-slate-400 font-medium">Pièce</th>
                                <th className="py-2 text-slate-400 font-medium">Réf.</th>
                                <th className="py-2 text-slate-400 font-medium">Marque</th>
                                <th className="py-2 text-slate-400 font-medium text-center">Qté</th>
                                <th className="py-2 text-slate-400 font-medium text-right">P.U.</th>
                                <th className="py-2 text-slate-400 font-medium text-right">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.partsUsed.map((part, idx) => (
                                <tr key={idx} className="border-b border-slate-800">
                                  <td className="py-2 text-white">{part.description || '—'}</td>
                                  <td className="py-2 text-slate-400">{part.reference || '—'}</td>
                                  <td className="py-2 text-slate-400">{part.brand || '—'}</td>
                                  <td className="py-2 text-white text-center">{part.quantity || 1}</td>
                                  <td className="py-2 text-white text-right">{(part.price || 0).toLocaleString('fr-FR')}</td>
                                  <td className="py-2 text-white text-right">{((part.price || 0) * (part.quantity || 1)).toLocaleString('fr-FR')} FCFA</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Notes */}
                    {entry.notes && (
                      <div className="mt-2">
                        <span className="text-slate-500 text-xs block mb-1">Notes</span>
                        <p className="text-slate-300 text-sm bg-slate-800/50 rounded-lg p-3">{entry.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WorkshopVehicleHistory;
